import { baseApi } from 'sama-shared';
import type {
  ApproveInvoicePayoutResult,
  ApproveInvoicePayoutsDto,
  GetPartnerInvoicesParams,
  InvoiceCycleResult,
  PaginatedPartnerInvoices,
  PartnerInvoice,
  PartnerInvoiceDetail,
  RunInvoiceCycleDto,
  VoidPartnerInvoiceDto,
} from 'sama-shared';

/**
 * Netting de settlements — PartnerInvoice (admin).
 * Ref: SETTLEMENTS_NETTING.md §5-§6.
 *
 * La factura es el agrupador contable; la pierna bancaria sigue siendo el
 * Payout / PartnerPayout. `issue()` no mueve dinero: el despacho al
 * payment-core es el acto explícito `approve-payouts`.
 *
 * Los nombres de endpoint están congelados en `baseApi.globalEndpoints`
 * (exención de `x-partner-location-id`, también bajo impersonación).
 * No renombrar sin actualizar esa lista.
 */
export const partnerInvoiceApi = baseApi
  .enhanceEndpoints({ addTagTypes: ['PartnerInvoice', 'PartnerCollectPayout'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getAdminPartnerInvoices: builder.query<
        PaginatedPartnerInvoices,
        GetPartnerInvoicesParams | void
      >({
        query: (params) => ({
          url: '/admin/partner-invoice',
          method: 'GET',
          params: params || {},
        }),
        providesTags: (result) =>
          result?.data
            ? [
                ...result.data.map(({ id }) => ({ type: 'PartnerInvoice' as const, id })),
                { type: 'PartnerInvoice' as const, id: 'LIST' },
              ]
            : [{ type: 'PartnerInvoice' as const, id: 'LIST' }],
      }),

      getAdminPartnerInvoiceById: builder.query<PartnerInvoiceDetail, string>({
        query: (id) => ({
          url: `/admin/partner-invoice/${id}`,
          method: 'GET',
        }),
        providesTags: (_result, _error, id) => [{ type: 'PartnerInvoice', id }],
      }),

      /**
       * Genera los DRAFTs del día. DRAFT no congela: es vista previa y se
       * recalcula al emitir. Un partner que falla no aborta el lote
       * (ver `InvoiceCycleResult`: `skipped`/`error` por fila).
       */
      runInvoiceCycle: builder.mutation<InvoiceCycleResult, RunInvoiceCycleDto | void>({
        query: (body) => ({
          url: '/admin/partner-invoice/run-cycle',
          method: 'POST',
          body: body || {},
        }),
        invalidatesTags: [{ type: 'PartnerInvoice', id: 'LIST' }],
      }),

      /**
       * DRAFT → ISSUED (+ PENDING_* en la misma tx). Congela settlements
       * (AVAILABLE → PROCESSING) y crea la pierna. Fuera de DRAFT da 400.
       * 409 = cierre concurrente, reintentable.
       */
      issuePartnerInvoice: builder.mutation<PartnerInvoice, string>({
        query: (id) => ({
          url: `/admin/partner-invoice/${id}/issue`,
          method: 'POST',
        }),
        invalidatesTags: (_result, _error, id) => [
          { type: 'PartnerInvoice', id },
          { type: 'PartnerInvoice', id: 'LIST' },
          { type: 'Settlement', id: 'LIST' },
          { type: 'PartnerCollectSettlement', id: 'LIST' },
          { type: 'Payout', id: 'LIST' },
          { type: 'PartnerCollectPayout', id: 'LIST' },
        ],
      }),

      /**
       * Despacho explícito al payment-core. No mueve la factura de estado por
       * sí solo: el SETTLED llega por webhook (propagación §7). Una factura
       * que falla no detiene al resto (`ApproveInvoicePayoutResult.error`).
       */
      approveInvoicePayouts: builder.mutation<
        ApproveInvoicePayoutResult[],
        ApproveInvoicePayoutsDto
      >({
        query: (body) => ({
          url: '/admin/partner-invoice/approve-payouts',
          method: 'POST',
          body,
        }),
        invalidatesTags: (_result, _error, { invoiceIds }) => [
          ...invoiceIds.map((id) => ({ type: 'PartnerInvoice' as const, id })),
          { type: 'PartnerInvoice', id: 'LIST' },
          { type: 'Payout', id: 'LIST' },
          { type: 'PartnerCollectPayout', id: 'LIST' },
        ],
      }),

      /**
       * Libera settlements (`invoiceId = null`, → AVAILABLE). Falla con
       * pierna viva (400): hay que resolver la pierna primero.
       */
      voidPartnerInvoice: builder.mutation<
        PartnerInvoice,
        { id: string; body: VoidPartnerInvoiceDto }
      >({
        query: ({ id, body }) => ({
          url: `/admin/partner-invoice/${id}/void`,
          method: 'POST',
          body,
        }),
        invalidatesTags: (_result, _error, { id }) => [
          { type: 'PartnerInvoice', id },
          { type: 'PartnerInvoice', id: 'LIST' },
          { type: 'Settlement', id: 'LIST' },
          { type: 'PartnerCollectSettlement', id: 'LIST' },
        ],
      }),

      /**
       * Cancela la pierna Sama → partner (solo PENDING/PROCESSING). El Payout
       * queda FAILED, los settlements vuelven a AVAILABLE y la factura pasa a
       * VOID: es la forma de anular una factura ya emitida (§7.2).
       */
      cancelInvoicePayout: builder.mutation<unknown, { payoutId: string; invoiceId: string }>({
        query: ({ payoutId }) => ({
          url: `/admin/payout/${payoutId}/cancel`,
          method: 'POST',
        }),
        invalidatesTags: (_result, _error, { invoiceId }) => [
          { type: 'PartnerInvoice', id: invoiceId },
          { type: 'PartnerInvoice', id: 'LIST' },
          { type: 'Settlement', id: 'LIST' },
          { type: 'PartnerCollectSettlement', id: 'LIST' },
          { type: 'Payout', id: 'LIST' },
        ],
      }),

      /** DISPUTED → PENDING_* según `direction`. No toca settlements. */
      resolveInvoiceDispute: builder.mutation<PartnerInvoice, string>({
        query: (id) => ({
          url: `/admin/partner-invoice/${id}/resolve-dispute`,
          method: 'POST',
        }),
        invalidatesTags: (_result, _error, id) => [
          { type: 'PartnerInvoice', id },
          { type: 'PartnerInvoice', id: 'LIST' },
        ],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetAdminPartnerInvoicesQuery,
  useGetAdminPartnerInvoiceByIdQuery,
  useRunInvoiceCycleMutation,
  useIssuePartnerInvoiceMutation,
  useApproveInvoicePayoutsMutation,
  useVoidPartnerInvoiceMutation,
  useResolveInvoiceDisputeMutation,
  useCancelInvoicePayoutMutation,
} = partnerInvoiceApi;
